/**
 * M7 S25: "on the payroll" (spec §8, §11; T7 data). A player who took **both** cash-for-silence exits
 * (`manifest_sold` and `hale_deal`) and whose Case line is below Documented is not offered the finale: Tethys treats
 * them as staff, and the Wire notes it once. The state is re-derived, never stuck: the repeatable corroborations
 * (§2.5) can rebuild the case to Documented, which clears `on_payroll` and opens the finale (Kell says so on comms).
 * After the first finale commit the state no longer changes.
 *
 * Pure (Career in, Career out); event-driven (the link calls `syncPayroll` after any change to the case), never per
 * frame.
 */
import type { Career } from '../../career/types'
import { caseStrength } from '../case'
import type { CaseData } from '../bundle'
import type { FinaleConfig } from '../schemas'
import { clearFlag, hasFlag, setFlag, storyOf } from '../state'
import { CASE_LINES, type StoryResult } from '../types'
import { finaleRefusal, type FinaleRefusal } from './ladder'
import { finaleData } from './paths'

/** Kell's line when the case is rebuilt past "on the payroll" (`comms/finale.json`). */
export const PAYROLL_REBUILT_NODE = 'finale.payroll.rebuilt'

const DOCUMENTED = CASE_LINES.indexOf('documented')

/** Both cash exits taken. */
export function tookBothCashExits(career: Career): boolean {
  return hasFlag(career, 'manifest_sold') && hasFlag(career, 'hale_deal')
}

/** True while the player is "on the payroll": both cash exits and the best case below Documented. */
export function onPayroll(career: Career, data: CaseData = finaleData().case): boolean {
  if (!tookBothCashExits(career)) return false
  return CASE_LINES.indexOf(caseStrength(career, data).line) < DOCUMENTED
}

/**
 * Sets or clears `on_payroll` to match the case (before the first commit only). Newly set: the Wire note and the
 * world flag. Cleared (the case rebuilt): the world flag and Kell's line.
 */
export function syncPayroll(career: Career, cfg: FinaleConfig = finaleData().finale, data: CaseData = finaleData().case): StoryResult<Career> {
  if (storyOf(career).finale.paths.length > 0) return { career, commands: [], effects: [] }
  const was = hasFlag(career, 'on_payroll')
  const now = onPayroll(career, data)
  if (was === now) return { career, commands: [], effects: [] }
  if (now) {
    return {
      career: setFlag(career, 'on_payroll'),
      commands: [
        { type: 'setWorldFlag', key: 'on_payroll', value: true },
        { type: 'postWire', headline: cfg.payroll.headline, severity: cfg.payroll.severity, source: cfg.payroll.source },
      ],
      effects: [],
    }
  }
  return {
    career: clearFlag(career, 'on_payroll'),
    commands: [{ type: 'setWorldFlag', key: 'on_payroll', value: false }],
    effects: [{ type: 'comms', speaker: 'kell', nodeId: PAYROLL_REBUILT_NODE }],
  }
}

/** Why the finale isn't offered: the ladder's refusals, plus `onPayroll` before the first commit. */
export type FinaleOfferRefusal = FinaleRefusal | 'onPayroll'

/**
 * The finale offer gate the UI uses (S39): `ladder.ts finaleRefusal`, and before the first commit "on the payroll"
 * refuses every path.
 */
export function finaleOfferRefusal(
  career: Career,
  to: Parameters<typeof finaleRefusal>[1],
  cfg: FinaleConfig = finaleData().finale,
  data: CaseData = finaleData().case,
): FinaleOfferRefusal | null {
  const r = finaleRefusal(career, to, cfg)
  if (r === 'notOffered') return r
  if (storyOf(career).finale.paths.length === 0 && onPayroll(career, data)) return 'onPayroll'
  return r
}
